import type { ListingVariant } from "../listing/types";
import { isJsonObject, type JsonObject, type JsonValue, jsonPath } from "../shared/json";

/**
 * A sneaker size read as a US M number, from the size chart the StockX catalog joins onto each
 * variant. The size range (ENG-7332) and the later size filters all compare in US M, whatever
 * system the product sells in: a women's or youth product is judged through its variants' own
 * `us m` conversions, so every product lands on one scale.
 */

/**
 * The US men's system, as {@link toSizeSystem} names StockX's `us m` size chart type. A variant
 * with no size chart is keyed in it too: its `variantValue` is read as US M.
 */
const US_M_SIZE_SYSTEM = "US M";

/** A range of US M sizes, both ends included. */
export interface UsMSizeRange {
  min: number;
  max: number;
}

/**
 * The sizes a sneaker listing stores: US M 4.5 (US W 6) to 13, chosen by Kai on 2026-10-15 from
 * 7 to 13. Shoppers outside it cannot pick their size, and an outlier size outside it no longer
 * sets the one price.
 */
export const DEFAULT_SNEAKER_SIZE_RANGE: Readonly<UsMSizeRange> = { min: 4.5, max: 13 };

/**
 * The sizes a full size run needs when no size band is named: US M 7 to 13. Fewer products sell
 * US M 4.5 to 6.5 at all, so requiring them would leave out most products.
 */
export const FULL_SIZE_RUN_RANGE: Readonly<UsMSizeRange> = { min: 7, max: 13 };

/** One US M size band: the sizes a band row sells, both ends included. */
export interface SneakerSizeBand extends UsMSizeRange {
  /** The band's segment in a listing id, `STOCKX#<urlKey>#b:<id>`, such as "US-M-10-10.5". */
  id: string;
  /** The band as a shopper reads it, such as "US M 10-10.5". */
  label: string;
}

/** Name an inclusive US M range for display and for the listing ID segment. */
function usMSizeBand(min: number, max: number): SneakerSizeBand {
  return { id: `US-M-${min}-${max}`, label: `US M ${min}-${max}`, min, max };
}

/**
 * The size bands a sneaker product can have one listing row each for, chosen by Kai on
 * 2026-09-25: the 5 Courtyard bands (8-8.5 to 12-13) plus 7-7.5 and, since 2026-10-15, 4.5-6.5,
 * which cover the rest of {@link DEFAULT_SNEAKER_SIZE_RANGE}. A variant joins a band through its `us m` conversion
 * ({@link readUsMSize}), and a Wide size joins the band of its number, so a band row holds every
 * size of the whole-product row that lies in the band. Women's and youth bands are deferred.
 */
export const SNEAKER_SIZE_BANDS: readonly SneakerSizeBand[] = [
  usMSizeBand(4.5, 6.5),
  usMSizeBand(7, 7.5),
  usMSizeBand(8, 8.5),
  usMSizeBand(9, 9.5),
  usMSizeBand(10, 10.5),
  usMSizeBand(11, 11.5),
  usMSizeBand(12, 13),
];

/** The band a US M size lies in, or null for a size outside every band or no size. */
export function sneakerSizeBandOf(size: number | null): SneakerSizeBand | null {
  return SNEAKER_SIZE_BANDS.find((band) => isInUsMSizeRange(size, band)) ?? null;
}

/** The band with this listing id segment, or null for an id no band has. */
export function sneakerSizeBandById(bandId: string): SneakerSizeBand | null {
  return SNEAKER_SIZE_BANDS.find((band) => band.id === bandId) ?? null;
}

/**
 * A bare or prefixed whole or half size: "10", "10.5" or "US M 10.5", with an optional " Wide"
 * after it ("US M 10.5 Wide"). The catalog sends the bare form in `defaultConversion` and the
 * prefixed one in `availableConversions`. A youth or child size ("7Y", "3.5C") does not match,
 * since it is not a men's size; nor does any other width, such as "Extra Wide".
 */
const US_M_SIZE_PATTERN = /^(?:US M\s+)?(\d+(?:\.5)?)(\s+Wide)?$/i;

/** One variant's US M size, and whether it came from `variantValue` for want of a size chart. */
export interface UsMSizeRead {
  /** The US M size, or null when the variant has no `us m` row or its size does not parse. */
  size: number | null;
  /**
   * True for a Wide size ("US M 10.5 Wide"). Its `size` is its number, so the size range and the
   * size bands place it as that size, but it is not that size: its size key stays
   * "US M 10.5 Wide", and a full size run does not count it.
   */
  isWide: boolean;
  /** True when the variant stored no size chart, so `variantValue` was read as a US M size. */
  fromVariantValue: boolean;
}

/**
 * The variant's US M size. It reads the `us m` row of the stored size chart, the default
 * conversion first, with the row's type compared in the index's case ("us m" and "US M" match). A variant with no size chart at all has its `variantValue` read as US M, the
 * rule the pre-work decisions set for a null chart; the caller logs that. A chart with no `us m`
 * row gives a null size.
 */
export function readUsMSize(
  variant: Pick<ListingVariant, "variantValue" | "details">,
): UsMSizeRead {
  const sizeChart = jsonPath(variant.details, "sizeChart");
  if (!isJsonObject(sizeChart)) {
    return { ...parseUsMSize(variant.variantValue), fromVariantValue: true };
  }
  const usM = conversionsOf(sizeChart).find((conversion) => {
    const type = jsonPath(conversion, "type");
    return typeof type === "string" && toSizeSystem(type) === US_M_SIZE_SYSTEM;
  });
  return { ...parseUsMSize(usM ? jsonPath(usM, "size") : null), fromVariantValue: false };
}

/** Whether a US M size lies inside the range, both ends included. */
export function isInUsMSizeRange(size: number | null, range: Readonly<UsMSizeRange>): boolean {
  return size !== null && size >= range.min && size <= range.max;
}

/** Whether a variant's US M size ({@link readUsMSize}) lies inside the range, both ends included. */
export function isVariantInUsMSizeRange(
  variant: Pick<ListingVariant, "variantValue" | "details">,
  range: Readonly<UsMSizeRange>,
): boolean {
  return isInUsMSizeRange(readUsMSize(variant).size, range);
}

/**
 * Every whole and half US M size in the range, both ends included: 7 to 13 gives 7, 7.5 ... 13,
 * 13 sizes. A full size run (ENG-7328) needs a variant in each of them.
 */
export function usMSizesIn(range: Readonly<UsMSizeRange>): number[] {
  const sizes: number[] = [];
  for (let halfSizes = range.min * 2; halfSizes <= range.max * 2; halfSizes += 1) {
    sizes.push(halfSizes / 2);
  }
  return sizes;
}

/**
 * The index size keys of every US M size in the range ("US M 7", "US M 7.5" ... "US M 13"), in
 * the form `readVariantSizeKeys` writes them.
 */
export function usMSizeKeysIn(range: Readonly<UsMSizeRange>): string[] {
  return usMSizesIn(range).map((size) => `${US_M_SIZE_SYSTEM} ${size}`);
}

/**
 * The US M sizes in the range that no variant sells, smallest first. An empty result means the
 * variants hold a full size run of the range. Each variant is read through its `us m`
 * conversion ({@link readUsMSize}). A Wide size does not stand in for its regular size, as in
 * the index's full size run filter, where "US M 10.5 Wide" is not the key "US M 10.5".
 */
export function missingUsMSizes(
  variants: readonly Pick<ListingVariant, "variantValue" | "details">[],
  range: Readonly<UsMSizeRange>,
): number[] {
  const soldSizes = new Set(
    variants
      .map((variant) => readUsMSize(variant))
      .filter(({ isWide }) => !isWide)
      .map(({ size }) => size),
  );
  return usMSizesIn(range).filter((size) => !soldSizes.has(size));
}

/** The chart's default conversion, then every available conversion. */
function conversionsOf(sizeChart: JsonObject): JsonValue[] {
  const available = sizeChart.availableConversions;
  return [sizeChart.defaultConversion ?? null, ...(Array.isArray(available) ? available : [])];
}

/**
 * A size string as a US M number and whether it is Wide. The size is null when the string is not
 * a whole or half size, bare or Wide.
 */
function parseUsMSize(value: JsonValue): Pick<UsMSizeRead, "size" | "isWide"> {
  const match = typeof value === "string" ? US_M_SIZE_PATTERN.exec(value.trim()) : null;
  if (!match?.[1]) return { size: null, isWide: false };
  return { size: Number(match[1]), isWide: Boolean(match[2]) };
}

/**
 * StockX's conversion `type` as a size system name: "us m" becomes "US M", "eu" becomes "EU".
 * Boxed builds its size labels with the same rule (`listingVariants.ts` in boxed-backend), so a
 * label Boxed shows matches a size key this index holds.
 */
export function toSizeSystem(type: string): string {
  return type.trim().replace(/\s+/g, " ").toUpperCase();
}

/**
 * One conversion row as a size key, "SYSTEM SIZE" ("US M 10", "EU 44"), or null when a field is
 * missing. The catalog sends the default size bare ("10") and each available size with its
 * system in front ("US M 10"), so a leading system name is removed before the two are joined.
 */
function toSizeKey(conversion: JsonValue): string | null {
  const type = jsonPath(conversion, "type");
  const size = jsonPath(conversion, "size");
  if (typeof type !== "string" || typeof size !== "string") return null;
  const system = toSizeSystem(type);
  const trimmedSize = size.trim();
  const bareSize = trimmedSize.toUpperCase().startsWith(`${system} `)
    ? trimmedSize.slice(system.length).trim()
    : trimmedSize;
  return system && bareSize ? `${system} ${bareSize}` : null;
}

/**
 * A Wide size key ("US M 10.5 Wide", in any case) ending in a whole or half size and " Wide".
 * "US M 10.5 Extra Wide" does not match, since only Wide is read as a size of its number.
 */
const WIDE_SIZE_KEY_PATTERN = /^(.*\d)\s+Wide$/i;

/**
 * The key of a Wide size's number ("US M 10.5 Wide" gives "US M 10.5"), or null for a key that
 * is not Wide. The index stores it in `wideSizes`, so a `size` filter for "US M 10.5" also finds
 * a listing whose only 10.5 is Wide, and `wide=true` finds the Wide sizes alone.
 */
export function wideSizeKeyBase(sizeKey: string): string | null {
  return WIDE_SIZE_KEY_PATTERN.exec(sizeKey.trim())?.[1]?.trim() ?? null;
}

/** One variant's size keys, and whether they came from `variantValue` for want of a size chart. */
export interface VariantSizeKeys {
  sizeKeys: string[];
  fromVariantValue: boolean;
}

/**
 * Every size key of one variant, one per conversion in its stored size chart, default first and
 * without repeats. A variant with no size chart gives "US M <variantValue>", the rule the
 * pre-work decisions set for a null chart; the caller logs that.
 */
export function readVariantSizeKeys(
  variant: Pick<ListingVariant, "variantValue" | "details">,
): VariantSizeKeys {
  const sizeChart = jsonPath(variant.details, "sizeChart");
  if (!isJsonObject(sizeChart)) {
    const variantValue = variant.variantValue?.trim();
    return {
      sizeKeys: variantValue ? [`${US_M_SIZE_SYSTEM} ${variantValue}`] : [],
      fromVariantValue: true,
    };
  }
  const sizeKeys = new Set<string>();
  for (const conversion of conversionsOf(sizeChart)) {
    const sizeKey = toSizeKey(conversion);
    if (sizeKey) sizeKeys.add(sizeKey);
  }
  return { sizeKeys: [...sizeKeys], fromVariantValue: false };
}
